import type { CohortRow, FactRow, Source } from '../types/dashboard';
import { territories } from '../data/territories';
import { MEMBER_SEASONALITY, PARTICIPANT_SEASONALITY, PROGRAM_MIX, SPORT_MIX } from '../data/dimensions';
import { COHORT_SEASONS, LATEST_MONTH, MONTH_COUNT, monthParts } from './calendar';
import { COMBO_COUNT, comboProgram, comboSport } from './combos';
import { clamp, noise } from './random';

/**
 * Deterministic SAMPLE data. Every view reads from these same records,
 * so totals stay consistent across the dashboard. Nothing here is live.
 */

const SOURCES: Source[] = ['Digital', 'Local', 'Unknown'];

/** Illustrative share of members whose referrer value is not yet mapped to Digital or Local / Other. */
function unknownShare(tid: string): number {
  return clamp(0.08 + noise(`${tid}|unk`, 0.03), 0.04, 0.13);
}
const LAG_SHARES = [0.6, 0.26, 0.14];

function territoryMix(tid: string): number[] {
  const raw = Array.from(
    { length: COMBO_COUNT },
    (_, k) => SPORT_MIX[comboSport(k)] * PROGRAM_MIX[comboProgram(k)] * (1 + noise(`${tid}|mix|${k}`, 0.35))
  );
  const total = raw.reduce((a, b) => a + b, 0);
  return raw.map((v) => v / total);
}

/** Share of each territory's registrations by sport × program combo. */
export const TERRITORY_MIX = new Map<string, number[]>(territories.map((t) => [t.id, territoryMix(t.id)] as [string, number[]]));

function buildFacts(): FactRow[] {
  const rows: FactRow[] = [];
  for (const t of territories) {
    for (let m = t.launchMonth; m < MONTH_COUNT; m++) {
      const ramp = t.launchMonth > 0 ? clamp(0.35 + (m - t.launchMonth) * 0.11, 0.35, 1) : 1;
      const season = MEMBER_SEASONALITY[monthParts(m).month];
      const growth = Math.pow(1 + t.growth, m / 12);
      for (const a of t.aops) {
        const base = t.size * a.weight * season * growth * ramp;
        for (const src of SOURCES) {
          const key = `${a.id}|${m}|${src}`;
          const u = unknownShare(t.id);
          const share = src === 'Unknown' ? u : (src === 'Digital' ? t.digitalShare : 1 - t.digitalShare) * (1 - u);
          const members = Math.max(0, Math.round(base * share * (1 + noise(`${key}|nm`, 0.09))));
          const p = clamp(
            (src === 'Digital' ? 0.27 : src === 'Local' ? 0.42 : 0.33) + t.quality * 0.06 + a.qualityAdj + noise(`${key}|p`, 0.025),
            0.1,
            0.7
          );
          const eventual = members * p;
          const conv = LAG_SHARES.map((s, l) => (m + l <= LATEST_MONTH ? Math.round(eventual * s) : 0));
          const rpm = 1.3 + (src === 'Local' ? 0.08 : 0) + t.quality * 0.05 + noise(`${key}|rpm`, 0.06);
          const regs = conv.map((c) => Math.round(c * rpm));
          rows.push({ t: t.id, a: a.id, m, src, members, conv, regs });
        }
      }
    }
  }
  return rows;
}

function buildCohorts(): CohortRow[] {
  const rows: CohortRow[] = [];
  for (const t of territories) {
    const mix = TERRITORY_MIX.get(t.id) ?? [];
    for (const sk of COHORT_SEASONS) {
      if (sk.startMonth + 2 < t.launchMonth) continue;
      const ramp = t.launchMonth > 0 ? clamp(0.4 + (sk.startMonth - t.launchMonth) * 0.1, 0.4, 1) : 1;
      const growth = Math.pow(1 + t.growth, sk.startMonth / 12);
      for (const a of t.aops) {
        for (let k = 0; k < COMBO_COUNT; k++) {
          const participants = Math.round(
            t.size * a.weight * 1.25 * PARTICIPANT_SEASONALITY[sk.season] * mix[k] * growth * ramp *
              (1 + noise(`${a.id}|${k}|${sk.idx}|pp`, 0.12))
          );
          if (participants <= 0) continue;
          const regRows = Math.max(participants, Math.round(participants * (1.16 + noise(`${a.id}|${k}|${sk.idx}|rr`, 0.05))));
          rows.push({ t: t.id, a: a.id, k, s: sk.idx, participants, regRows });
        }
      }
    }
  }
  return rows;
}

export const FACTS = buildFacts();
export const COHORTS = buildCohorts();
export const cohortKey = (a: string, k: number, s: number) => `${a}|${k}|${s}`;
export const COHORT_INDEX = new Map<string, CohortRow>(COHORTS.map((r) => [cohortKey(r.a, r.k, r.s), r] as [string, CohortRow]));
export const COHORTS_BY_SEASON: CohortRow[][] = COHORT_SEASONS.map((sk) => COHORTS.filter((r) => r.s === sk.idx));
